import { JobOperation } from '@commenda/api';
import { Badge, Button, Dropdown, Icons } from '@commenda/kakashi';

import { IndirectTaxJobsLogDialog, IndirectTaxRegion } from '../../common';
import { IndirectTaxFilingActionKey } from '../helpers';
import { useIndirectTaxFilingActionsMenu } from './useIndirectTaxFilingActionsMenu';

const ICON_BY_ACTION: Record<
  IndirectTaxFilingActionKey,
  typeof Icons.DotsVertical
> = {
  [IndirectTaxFilingActionKey.GENERATE_BREAKDOWNS]: Icons.PlaySquare,
  [IndirectTaxFilingActionKey.VIEW_GENERATED_REPORTS]: Icons.ReceiptCheck,
  [IndirectTaxFilingActionKey.EXPORT_BREAKDOWN]: Icons.FileDownload03,
  [IndirectTaxFilingActionKey.EXPORT_BREAKDOWN_WITH_LINE_ITEMS]:
    Icons.FileDownload03,
  [IndirectTaxFilingActionKey.GENERATE_REPORT]: Icons.PlaySquare,
  [IndirectTaxFilingActionKey.EXPORT_REPORT]: Icons.Download01,
  [IndirectTaxFilingActionKey.EXPORT_JURISDICTION_TAX_REPORT]:
    Icons.FileDownload03,
};

export interface IndirectTaxFilingActionsMenuProps {
  filingId: string;
  region: IndirectTaxRegion;
  corporationId: number;
  jurisdictionId: string;
  userEmail?: string;
  /** Force the menu visible regardless of viewer (agent app). */
  showInternalActions?: boolean;
  onActionComplete?: () => void;
}

/**
 * Region-aware kebab menu of report actions on the filing detail page.
 * US/CA filings get four breakdown actions, EU/UK filings get two
 * contribution actions, and the whole menu is hidden from non-Commenda
 * viewers unless `showInternalActions` forces it on.
 */
export function IndirectTaxFilingActionsMenu({
  filingId,
  region,
  corporationId,
  jurisdictionId,
  userEmail,
  showInternalActions,
  onActionComplete,
}: IndirectTaxFilingActionsMenuProps) {
  const { isInternal, actions, jobsLogDialogVisibility, exportJobs } =
    useIndirectTaxFilingActionsMenu({
      filingId,
      region,
      corporationId,
      jurisdictionId,
      userEmail,
      showInternalActions,
      onActionComplete,
    });

  if (!isInternal || actions.length === 0) {
    return null;
  }

  return (
    <>
      <Dropdown
        align="end"
        itemClassName="py-md px-lg"
        contentClassName="w-76"
        menu={{
          groups: [
            {
              items: actions.map((action) => ({
                icon: ICON_BY_ACTION[action.key],
                label: action.label,
                disabled: action.disabled,
                onClick: action.onClick,
                shortcut: (
                  <Badge variant="gray" size="sm">
                    Internal
                  </Badge>
                ),
              })),
            },
          ],
        }}
      >
        <Button variant="tertiary" size="sm">
          <Icons.DotsVertical className="size-4" />
        </Button>
      </Dropdown>

      <IndirectTaxJobsLogDialog
        isOpen={jobsLogDialogVisibility.visible}
        onClose={jobsLogDialogVisibility.hide}
        jobs={exportJobs}
        corporationId={corporationId}
        jobOperation={JobOperation.EXPORT}
        title="Tax breakdown reports"
      />
    </>
  );
}
